import { useEffect, useMemo, useState } from "react";
import styles from "./Subjects.module.scss";
import { Button, Form, Modal, Select, Spin, Table, Tag, Tooltip, Typography } from "antd";
import Layout from "../../Containers/Layout";
import Search from 'antd/lib/input/Search';
import { useQuery, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ApiOutlined, BarChartOutlined, CheckCircleOutlined, DesktopOutlined, DownloadOutlined, EyeOutlined, GroupOutlined, PlusOutlined, ProfileOutlined, ReloadOutlined, UserOutlined } from "@ant-design/icons";
import { Link, useHistory, useParams } from "react-router-dom";
import { theme } from "../../theme";
import { getUserId, useIsAdmin, useIsDean, useIsGenericDepartmentDirector, useIsDirectorCsud, useUserData, useCurrentUserFacultyId } from "../../utils/utilFunctions";
import { NOTIFICATION_TYPES, openNotification } from "../Notifications/NotificationsUtils";
import { assignEAFSGAreas, assignLaboratories, assignLearnings, deleteSubject, downloadDocFile, downloadPdfFile, getShortSubjects, sendSubjectMessage, getSubject, updateSubjectStatus } from "../../Requests/subject-requests";
import SubjectMessageModal from "./SubjectMessageModal";
import SubjectCoordinatorsModal from "./SubjectCoordinatorsModal";
import SubjectStatusHistoryModal from "./SubjectStatusHistoryModal";
import { getEAFSGAreas } from "../../Requests/eafsg-area-requests";
import { SubjectFileDTO, SemesterTypeEnum, StudyYearEnum, StudyCycleEnum, IndicatorTypeEnum, StudyPlanDTO, StudyPlanStatusEnum, Subject, SubjectDTO, DepartmentDTO, LearningCompetenceSimpleTypeEnum, DisciplineCategoryEnum, SubjectStatusEnum, AccreditationTypeEnum, LearningCompetenceDTO } from "../../Api";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { getSchoolYear, getSchoolYearRange, getStudyYearOptions, parseSubjectStatusValidationErrors, planOptions, semesterOptions } from "./helpers";
import { getLaboratoriesByUserFaculty } from "../../Requests/laboratory-requests";
import useDebounce from "../../Hooks/debounce";
import { getAllDepartmentsForFaculty } from "../../Requests/departement-requests";
import { ApproveBulkSubjects, downloadExtendedStudyPlan, downloadStudyPlan, downloadStudyPlanDiff, downloadStudyPlanIndicators, getAllStudyPlans, updateStudyPlanDepartment, updateStudyPlanStatus } from "../../Requests/study-plan-requests";
import { downloadFile, MimeType } from "../../utils/downloadUtils";
import { getLearningCompetences, getStudyProgram } from "../../Requests/study-program-request";

const { Option } = Select;

const PLAN_FILTERS_KEY = "planFilters";

const setPlanFilter = (programId: string, filters: any) => {
  const stored = localStorage.getItem(PLAN_FILTERS_KEY);
  const planFilters = stored ? JSON.parse(stored) : {};
  planFilters[programId] = filters;
  localStorage.setItem(PLAN_FILTERS_KEY, JSON.stringify(planFilters));
}

const getPlanFilter = (programId: string) => {
  const stored = localStorage.getItem(PLAN_FILTERS_KEY);
  if (!stored) return null;
  const planFilters = JSON.parse(stored);
  return planFilters[programId] ?? null;
}

const Subjects = () => {
    const { t } = useTranslation();
    const [searchTermNameRO, setSearchTermNameRO] = useState('');
    const [isCadru, setIsCadru] = useState(false);
    const [facultyId, setFacultyId] = useState<string | undefined>(undefined);
    const [selectedPlan, setSelectedPlan] = useState<StudyPlanDTO | undefined>(undefined);
    const [coordinatorsSubject, setCoordinatorsSubject] = useState<{ id?: string; nameRO?: string | null } | undefined>(undefined);

    const [schoolYear, setSchoolYear] = useState<number | undefined>(getSchoolYear(new Date()));
    const [planYear, setPlanYear] = useState<number | undefined>(undefined);
    const [semester, setSemester] = useState<SemesterTypeEnum>(SemesterTypeEnum.S1);
    const [studyYear, setStudyYear] = useState<StudyYearEnum>(StudyYearEnum.L1);
    const [searchTermName, setLabSearchTerm] = useState('');
    const [searchLeariningName, setLeariningSearchTerm] = useState('');
    const [searchEafsgAreaName, setEafsgAreaSearchTerm] = useState('');
	const debouncedLabSearch = useDebounce(searchTermName, 1000);
    const debouncedLearningSearch = useDebounce(searchLeariningName, 1000);
    const debouncedEafsgAreaSearch = useDebounce(searchEafsgAreaName, 1000);

    const history = useHistory();
    const isAdmin = useIsAdmin();
    // Doar rolul; decanul efectiv (al facultatii programului) e isDean, calculat dupa incarcarea programului
    const isDeanRole = useIsDean();
    const currentUserFacultyId = useCurrentUserFacultyId();
    const isDepartmentDirector = useIsGenericDepartmentDirector();
    const isDirectorCsud = useIsDirectorCsud();
    const currentUserDepartmentId = useUserData().data?.departmentId;
    // Rolul de director singur nu ajunge: un director ajunge si pe programele altor departamente
    // (ex. ca titular), unde nu are drepturi de director. Planul si fisa au departamentId separate.
    const isDirectorOfDepartment = (departmentId?: string | null) =>
        !!isDepartmentDirector
        && !!departmentId
        && departmentId !== '00000000-0000-0000-0000-000000000000'
        && departmentId === currentUserDepartmentId;
    const isPlanDepartmentDirector = isDirectorOfDepartment(selectedPlan?.departmentId);
    // Director al departamentului fisei SAU al planului ei (ca IsDepartmentDirectorOfSubject din backend). Planul se ia
    // de pe rand (planDepartmentId), nu din selectedPlan: in "plan curent" randurile vin din planuri cadru diferite,
    // iar selectedPlan e nesetat sau ramas de la ultimul plan cadru vizualizat.
    const isSubjectDepartmentDirector = (record: { departmentId?: string | null; planDepartmentId?: string | null }) =>
        isDirectorOfDepartment(record.departmentId) || isDirectorOfDepartment(record.planDepartmentId);
    const { programId, studyCycle } = useParams<{ programId: string, studyCycle: StudyCycleEnum }>();
    const isDoctorate = studyCycle === StudyCycleEnum.Doctorate;
    const canManageDoctorate = isAdmin || (isDirectorCsud && isDoctorate);

    const [form] = Form.useForm<{ assignedLabs: string[]; assignedLearnings: string[]; assignedEAFSGAreas: string[] }>();

    const [filters, setFilters] = useState<{year: StudyYearEnum, semester: SemesterTypeEnum, plan: string}>
    (() => {
            return getPlanFilter(programId) 
            ?? {
                year: studyCycle == StudyCycleEnum.Master
                    ? StudyYearEnum.M1
                    : studyCycle == StudyCycleEnum.Doctorate
                        ? StudyYearEnum.D1
                        : StudyYearEnum.L1, 
                semester: SemesterTypeEnum.S1,
                plan: getSchoolYearRange(1)[0].toString()
            };
    });

    

    useEffect(() => {
        handlePlanChange(filters.plan);
        setPlanFilter(programId, filters);
    }, [filters]);

    const [modalPropsReject, setModalPropsReject] = useState({
        isModalVisible: false,
        title: '',
        message: '',
        status: StudyPlanStatusEnum.Draft,
    });

    const [modalPropsRejectDiscipline, setModalPropsRejectDiscipline] = useState({
        isModalVisible: false,
        title: '',
        message: '',
        subjectId: '',
    });

    const [modalPropsSendMessage, setModalPropsSendMessage] = useState({
        isModalVisible: false,
        title: '',
        message: '',
        subjectId: '',
    });

    const [modalPropsHistory, setModalPropsHistory] = useState({
        isModalVisible: false,
        subjectId: '',
        subjectName: '',
    });


    const [modalPropsDelete, setModalPropsDelete] = useState({
        isModalVisible: false,
        title: '',
        id: '',
    });

    const [modalProps, setModalProps] = useState<{ isModalVisible: boolean, title: string, record: SubjectFileDTO }>({
        isModalVisible: false,
        title: '',
        record: {} as SubjectFileDTO
    });

    const [modalPropsLearnings, setModalPropsLearnings] = useState<{ isModalVisible: boolean, title: string, record: SubjectDTO }>({
        isModalVisible: false,
        title: '',
        record: {} as SubjectFileDTO
    });

    const [modalPropsEafsgAreas, setModalPropsEafsgAreas] = useState<{ isModalVisible: boolean, title: string, record: SubjectDTO }>({
        isModalVisible: false,
        title: '',
        record: {} as SubjectFileDTO
    });

    const [modalPropsValidationErrors, setModalPropsValidationErrors] = useState<{
        isModalVisible: boolean,
        generalErrors: string[],
        subjectsMissingLearningResults: string[],
        competencesMissingLearningResults: string[],
        subjectsMissingEAFSGAreas: string[],
    }>({
        isModalVisible: false,
        generalErrors: [],
        subjectsMissingLearningResults: [],
        competencesMissingLearningResults: [],
        subjectsMissingEAFSGAreas: [],
    });

    const handleCloseValidationErrors = () => {
        setModalPropsValidationErrors(modalProps => ({ ...modalProps, isModalVisible: false }));
    }

    const [modalPropsValidationErrorsDiscipline, setModalPropsValidationErrorsDiscipline] = useState<{ isModalVisible: boolean, errors: string[] }>({
        isModalVisible: false,
        errors: [],
    });

    const handleCloseValidationErrorsDiscipline = () => {
        setModalPropsValidationErrorsDiscipline(modalProps => ({ ...modalProps, isModalVisible: false }));
    };

    const handleShowDelete = (id: string) => {
        setModalPropsDelete(modalProps => ({ ...modalProps, isModalVisible: true, id }));
    }

    const handleCloseDelete = () => {
        setModalPropsDelete(modalProps => ({ ...modalProps, isModalVisible: false }));
    }

    const handleShowReject = (status: StudyPlanStatusEnum) => {
        setModalPropsReject(modalPropsReject => ({ ...modalPropsReject, isModalVisible: true, status }));
    }

    const handleCloseReject = () => {
        setModalPropsReject(modalPropsReject => ({ ...modalPropsReject, isModalVisible: false }));
    }

    const handleOkReject = () => {
        setModalPropsReject(modalPropsReject => ({ ...modalPropsReject, isModalVisible: false }));
        handleStatusChange(modalPropsReject.status, modalPropsReject.message)();
        setModalPropsReject(modalPropsReject => ({ ...modalPropsReject, message: '', status: StudyPlanStatusEnum.Draft }));

    }

    const handleDocDownload = (record: SubjectDTO) => {
        downloadFile(
            downloadDocFile(record.id!), "Fișă Disciplină " + record.nameRO, MimeType.docx 
        ).catch((err) => failed(err, 'Descărcarea nu a fost efectuată!'));
    }

    const handlePdfPreview = async (record: SubjectDTO) => {
        const win = window.open("about:blank", "_blank");
        if (!win) {
            failed(null, "Preview-ul nu a putut fi deschis!");
            return;
        }

        try {
            const res = await downloadPdfFile(record.id!);
            const blob = res instanceof Blob ? res : (res as any).data;

            const pdfBlob =
            blob instanceof Blob ? blob : new Blob([blob], { type: "application/pdf" });

            const url = URL.createObjectURL(pdfBlob);

            win.location.href = url;
            win.document.title = `Fișă Disciplină ${record.nameRO ?? ""}`.trim();

            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (err) {
            win.close();
            failed(err, "Preview-ul nu a putut fi deschis!");
        }
    };

    const handlePdfDownload = (record: SubjectDTO) => {
        downloadFile(
            downloadPdfFile(record.id!), "Fișă Disciplină " + record.nameRO 
        ).catch((err) => failed(err, 'Descărcarea nu a fost efectuată!'));
    }

    const handleApproveDiscipline = (state: SubjectStatusEnum, message: string, subjectId: string) => {
        handleStatusChangeDiscipline(state, message, subjectId)();
    }
    const handleRejectDiscipline = (subject: SubjectDTO) => {
        handleShowRejectDiscipline(subject);
    }

    const handleShowRejectDiscipline = (subject: SubjectDTO) => {
        setModalPropsRejectDiscipline(modalPropsRejectDiscipline => ({ ...modalPropsRejectDiscipline, isModalVisible: true, title: `Respinge fișa de disciplină pentru ${subject.nameRO}`, subjectId: subject.id! }));
    }

    const handleOkRejectDiscipline = () => {
        setModalPropsRejectDiscipline(modalPropsRejectDiscipline => ({ ...modalPropsRejectDiscipline, isModalVisible: false }));
        handleStatusChangeDiscipline(SubjectStatusEnum.Draft, modalPropsRejectDiscipline.message, modalPropsRejectDiscipline.subjectId)();
        setModalPropsRejectDiscipline(modalPropsRejectDiscipline => ({ ...modalPropsRejectDiscipline, message: '', subjectId: '' }));
    }

    const handleCloseRejectDiscipline = () => {
        setModalPropsRejectDiscipline(modalPropsRejectDiscipline => ({ ...modalPropsRejectDiscipline, isModalVisible: false }));
    }

    const handleShowSendMessage = (subject: SubjectDTO) => {
        setModalPropsSendMessage(modalPropsSendMessage => ({ ...modalPropsSendMessage, isModalVisible: true, title: `Transmite observații titularului disciplinei ${subject.nameRO}`, subjectId: subject.id! }));
    }

    const handleOkSendMessage = () => {
        setModalPropsSendMessage(modalPropsSendMessage => ({ ...modalPropsSendMessage, isModalVisible: false }));
        sendSubjectMessage(modalPropsSendMessage.subjectId, modalPropsSendMessage.message)
            .then(() => success('Mesajul a fost transmis cu succes!'))
            .catch((err) => failed(err, 'Mesajul nu a putut fi transmis!'));
        setModalPropsSendMessage(modalPropsSendMessage => ({ ...modalPropsSendMessage, message: '', subjectId: '' }));
    }

    const handleCloseSendMessage = () => {
        setModalPropsSendMessage(modalPropsSendMessage => ({ ...modalPropsSendMessage, isModalVisible: false }));
    }

    const handleShowHistory = (subject: SubjectDTO) => {
        setModalPropsHistory({ isModalVisible: true, subjectId: subject.id!, subjectName: subject.nameRO ?? '' });
    }

    const handleCloseHistory = () => {
        setModalPropsHistory(modalPropsHistory => ({ ...modalPropsHistory, isModalVisible: false }));
    }

    const handleStudyPlanPdfDownload = (id: string) => {
        downloadFile(
            downloadStudyPlan(id), selectedPlan?.fileName ?? 'Plan de învățământ', MimeType.pdf
        ).catch((err) => failed(err, 'Descărcarea nu a fost efectuată!'));
    }

    const handleExtendedStudyPlanPdfDownload = (id: string) => {
        downloadFile(
            downloadExtendedStudyPlan(id), selectedPlan?.fileName ?? 'Plan de învățământ', MimeType.pdf
        ).catch((err) => failed(err, 'Descărcarea nu a fost efectuată!'));
    }

    const handleStudyPlanDiffPdfDownload = (id: string) => {
        // Acelasi tipar ca la celelalte documente de plan (vezi StudyPlanDTO.FileName):
        // anii in fata, restul cu underscore. Ex: 2026_2025_Modificari_PLi_Automatica
        const comparedYears = selectedPlan?.planYear && previousPlanYear
            ? `${selectedPlan.planYear}_${previousPlanYear}_`
            : '';
        const fileName = comparedYears
            + 'Modificari_PLi'
            + (program?.name ? '_' + program.name.replace(/[\\/:*?"<>|]/g, '').trim().replace(/\s+/g, '_') : '');

        downloadFile(downloadStudyPlanDiff(id), fileName, MimeType.pdf)
            .catch((err) => failed(err, 'Descărcarea nu a fost efectuată!'));
    }

    // Tabul de preview se deschide gol și rămâne așa cât durează generarea PDF-ului pe server.
    // Nu îl putem deschide după ce documentul e gata: window.open trebuie apelat sincron, în gestul
    // de click, altfel îl blochează browserul. Deci îi scriem o pagină de așteptare.
    const writePreviewPlaceholder = (win: Window, heading: string, message: string, isLoading: boolean) => {
        win.document.open();
        win.document.write(`<!DOCTYPE html>
<html lang="ro">
<head>
    <meta charset="utf-8" />
    <title>${heading}</title>
    <style>
        body { margin: 0; height: 100vh; display: flex; align-items: center; justify-content: center;
               font-family: sans-serif; background: #fafafa; color: #444; }
        .box { text-align: center; padding: 24px; }
        .spinner { width: 38px; height: 38px; margin: 0 auto 18px; border: 4px solid #e0e0e0;
                   border-top-color: #1890ff; border-radius: 50%; animation: spin 0.9s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
        h1 { font-size: 16px; font-weight: 600; margin: 0 0 6px; }
        p { font-size: 13px; margin: 0; color: #888; }
    </style>
</head>
<body>
    <div class="box">
        ${isLoading ? '<div class="spinner"></div>' : ''}
        <h1>${heading}</h1>
        <p>${message}</p>
    </div>
</body>
</html>`);
        win.document.close();
    };

    const handleExtendedStudyPlanPdfPreview = async (id: string) => {
        const win = window.open("about:blank", "_blank");
        if (!win) {
            failed(null, "Preview-ul nu a putut fi deschis!");
            return;
        }

        writePreviewPlaceholder(
            win,
            "Se generează planul de învățământ…",
            "Documentul se va deschide automat în această filă.",
            true
        );

        try {
            const res = await downloadExtendedStudyPlan(id);
            const blob = res instanceof Blob ? res : (res as any).data;

            const pdfBlob =
            blob instanceof Blob ? blob : new Blob([blob], { type: "application/pdf" });

            const url = URL.createObjectURL(pdfBlob);

            win.location.href = url;
            win.document.title = selectedPlan?.fileName ?? 'Plan de învățământ';

            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (err) {
            // Mesajul rămâne în filă în loc ca fila să se închidă singură: utilizatorul se uită
            // cel mai probabil la ea, nu la fila aplicației unde apare notificarea de eroare.
            writePreviewPlaceholder(
                win,
                "Planul nu a putut fi generat.",
                "Puteți închide această filă și încerca din nou.",
                false
            );
            failed(err, "Preview-ul nu a putut fi deschis!");
        }
    };

    const handleStudyPlanIndicatorsPdfDownload = (id: string) => {
        downloadFile(
            downloadStudyPlanIndicators(id), selectedPlan?.fileName + "_Statistici", MimeType.pdf
        ).catch((err) => failed(err, 'Descărcarea nu a fost efectuată!'));
    }

    const handleApproveBulkSubjects = async (studyProgramId: string) => {
        if (!studyProgramId) return;
        try {
            await ApproveBulkSubjects(studyProgramId);
            success('Statusul a fost actualizat cu succes!');
            queryClient.invalidateQueries('getSubjects');
        } catch (err: any) {
            let errMsg = await err.text();
            failed(err, 'Statusul nu a putut fi actualizat! ' + errMsg);
        }
    }

    const handleOkDelete = () => {
        setModalPropsDelete(modalProps => ({ ...modalProps, isModalVisible: false }));
        deleteSubject(modalPropsDelete.id)
            .then(() => {
                success('Ștergerea a fost efectuată cu succes!');
                invalidateQuery();
            })
            .catch((err) => failed(err, 'Ștergerea nu a fost efectuată!'));
    }

    const handleUpdateLabs = () => {
        setModalProps(modalProps => ({ ...modalProps, isModalVisible: false }));
        setLabSearchTerm('');
        let labs = form.getFieldValue('assignedLabs');
        console.warn(labs);
        assignLaboratories({ subjectId: modalProps.record.id!, laboratoryIds: labs })
        .then(() => {
            success('Laboratoarele au fost actualizate cu succes!');
            invalidateQuery();
        })
        .catch((err) => failed(err, 'Laboratoarele nu au putut fi actualizate!'));
    }

    const handleUpdateLearnings = () => {
        setModalPropsLearnings(modalProps => ({ ...modalProps, isModalVisible: false }));
        setLeariningSearchTerm('');
        let labs = form.getFieldValue('assignedLearnings');
        assignLearnings({ subjectId: modalPropsLearnings.record.id!, learningIds: labs })
        .then(() => {
            success('Rezultatele învățarii au fost actualizate cu succes!');
            invalidateQuery();
        })
        .catch((err) => failed(err, 'Rezultatele învățarii nu au putut fi actualizate!'));
    }

    const handleUpdateEafsgAreas = () => {
        setModalPropsEafsgAreas(modalProps => ({ ...modalProps, isModalVisible: false }));
        setEafsgAreaSearchTerm('');
        let areas = form.getFieldValue('assignedEAFSGAreas');
        assignEAFSGAreas({ subjectId: modalPropsEafsgAreas.record.id!, areaIds: areas })
        .then(() => {
            success('Ariile EAFSG au fost actualizate cu succes!');
            invalidateQuery();
        })
        .catch((err) => failed(err, 'Ariile EAFSG nu au putut fi actualizate!'));
    }

    const success = (message: string) => {
        openNotification(
            'Succes',
            message,
            NOTIFICATION_TYPES.SUCCESS
        );
    };

    const failed = (_err: any, message: string) => {
        openNotification(
            'Eroare',
            message,
            NOTIFICATION_TYPES.ERROR
        );
    };

    const queryClient = useQueryClient();

    const invalidateQuery = () => { 
        queryClient.invalidateQueries('getSubjects'); 
        queryClient.invalidateQueries('getAllStudyPlans')
     };

    const openFetchError = (_error: any) => {
        openNotification(
            'Eroare!',
            'Datele nu au putut fi preluate de la server!',
            NOTIFICATION_TYPES.ERROR
        );
    }

    const { data, isLoading } = useQuery(['getSubjects', searchTermNameRO, schoolYear, planYear, filters],
        () => {
            const effectiveYear = isDoctorate ? StudyYearEnum.D1 : filters.year;
            const response = getShortSubjects(searchTermNameRO, schoolYear, planYear, filters.semester, effectiveYear, programId, 1, 500);
            return response;
        },
        {
            onError: openFetchError,
        }
    );
    
    const { data: plans } = useQuery(['getAllStudyPlans'],
      () => {
          const response = getAllStudyPlans(programId, 1, 10);
          
          return response;
      },
      {
          onError: openFetchError,
      }
    );

    const { data: departments } = useQuery(['getFacultyDepartments', plans, facultyId],
        async () => {
            //let facultyId = plans?.data?.find(plan => plan.planYear === parseInt(filters.plan.split(' ')[1]))?.facultyId;
            if (facultyId) {
                const response = getAllDepartmentsForFaculty(facultyId);
                return response;
            }
            return [];
        },
        {
            onError: openFetchError,
        }
      );

    
    // Planul anterior e cel cu anul imediat mai mic; fara el nu exista cu ce compara.
    const previousPlanYear = selectedPlan?.planYear
        ? (plans?.data ?? [])
            .map(plan => plan.planYear ?? 0)
            .filter(planYear => planYear < selectedPlan.planYear!)
            .sort((a, b) => b - a)[0]
        : undefined;
    const hasPreviousPlan = !!previousPlanYear;

    useEffect(() => {
        if (!isCadru) return;

        const parts = String(filters.plan).split(' ');
        if (parts[0] !== 'cadru') return;

        const year = parseInt(parts[1], 10);
        const plan = plans?.data?.find(p => p.planYear === year);

        if (plan) {
            setSelectedPlan(plan);
            setFacultyId(plan.facultyId);
        }
    }, [plans, isCadru, filters.plan]);

    useEffect(() => {
        if (!isDoctorate) return;
        if (String(filters.plan).startsWith('cadru')) return;

        const firstPlan = plans?.data?.[0];
        if (!firstPlan?.planYear) return;

        const planValue = 'cadru ' + firstPlan.planYear.toString();
        handlePlanChange(planValue);
        setFilters(f => ({ ...f, plan: planValue }));
    }, [plans, isDoctorate]);

    

    const handleClose = () => {
        setLabSearchTerm('');
        setModalProps(modalProps => ({...modalProps, isModalVisible: false}));
    }

    const handleCloseLearning = () => {
        setLeariningSearchTerm('');
        setModalPropsLearnings(modalProps => ({...modalProps, isModalVisible: false}));
    }

    const handleCloseEafsgAreas = () => {
        setEafsgAreaSearchTerm('');
        setModalPropsEafsgAreas(modalProps => ({...modalProps, isModalVisible: false}));
    }

    const FilterByNameROInput = (
        <Search placeholder={t('subject.nameRO')} allowClear onSearch={setSearchTermNameRO} style={{ width: '100%' }} />
    );

    const handleShowDetails = (record: SubjectFileDTO) => {
        if (!(canManageDoctorate || isDepartmentDirector || isDean || isProgramSupervisor)
            || record.courseCoordinators?.some(e => e.id === getUserId()) === false) {
            openNotification("Acces interzis", "Nu aveți permisiunea de a atribui laboratoare.", NOTIFICATION_TYPES.ERROR);
            return;
        }
        form.setFieldsValue({ assignedLabs: record.laboratories?.map(lab => lab.id) });
        setLabSearchTerm('');
        setModalProps(modalProps => ({...modalProps, isModalVisible: true, title: "Laboratoare", record}));
    }

    const handleShowLearningsDetails = (record: SubjectDTO) => {
        form.setFieldsValue({ assignedLearnings: record.learnings ?? [] });
        setLeariningSearchTerm('');
        setModalPropsLearnings({
            isModalVisible: true,
            title: "Rezultate învățare",
            record,
        });
    };

    const handleShowEafsgAreasDetails = (record: SubjectFileDTO) => {
        form.setFieldsValue({ assignedEAFSGAreas: record.eafsgAreas ?? [] });
        setEafsgAreaSearchTerm('');
        setModalPropsEafsgAreas({
            isModalVisible: true,
            title: "Arii EAFSG",
            record,
        });
    };

    const statusColors = (status: SubjectStatusEnum) => { // de modficat cu enumul corect 
        switch (status) {
            case SubjectStatusEnum.Draft: return 'red';
            case SubjectStatusEnum.PendingDirectorValidation: return 'magenta';
            case SubjectStatusEnum.PendingDeanValidation: return 'orange';
            case SubjectStatusEnum.PendingQaValidation: return 'gold';
            case SubjectStatusEnum.Approved: return 'green';
        }
    };

    const statusToString = (status: SubjectStatusEnum) => { // de modficat cu enumul corect
        switch (status) {
            case SubjectStatusEnum.Draft: return 'Draft';
            case SubjectStatusEnum.PendingDirectorValidation: return 'Validare Departament';
            case SubjectStatusEnum.PendingDeanValidation: return 'Validare Decan';
            case SubjectStatusEnum.PendingQaValidation: return 'Validare Calitate';
            case SubjectStatusEnum.Approved: return 'Aprobat';
        }
    };

    const { data: program } = useQuery(["getProgram", programId], () => getStudyProgram(programId), { onError: openFetchError }); 
    // Decan/prodecan doar pe programele facultatii lui; pe alte programe ajunge doar ca titular sau responsabil
    const isDean = !!isDeanRole && !!program?.facultyId && program.facultyId === currentUserFacultyId;

    const dualProgramHoursUniversityColor = "#B5E6A2";
    const dualProgramHoursPartnerColor = "#00FF99";

    const getDualHourBackground = (value: number, atPartner: boolean | undefined) => {
        if (value < 0) {
            return "#F8D7DA";
        }

        if (!program?.isDualProgram || value === 0) {
            return "transparent";
        }

        return atPartner ? dualProgramHoursPartnerColor : dualProgramHoursUniversityColor;
    };

    const dualCreditsTotals = (data?.data || []).reduce(
        (acc, subject) => {
            acc.iis += Number(subject.creditsIIS ?? 0);
            acc.oe += Number(subject.creditsOE ?? 0);
            return acc;
        },
        { iis: 0, oe: 0 }
    );

    let columns: any = [
        {
            title: 'Nr. Crt.',
            dataIndex: 'index',
            width: '50px',
            key: 'index',
            // render: (text: any, record: any, index: number) => index + 1,
            render: (text: any, record: any, index: number) => record.orderNumber,
        },
        {
            title: 'Codul Disciplinei',
            dataIndex: 'code',
            key: 'code',
        },
        {
            title: 'Denumirea Disciplinei',
            dataIndex: 'nameRO',
            key: 'nameRO',
        },
        {
            title: 'Categorie formativă',
            dataIndex: 'formativeCategory',
            key: 'formativeCategory',
            render: (text: any, record: any, index: number) => {
                return record.formativeCategory === IndicatorTypeEnum.Fundamental ? <Tag color='magenta'  key={'Fundamentală'}>Fundamentală</Tag>
                    : record.formativeCategory === IndicatorTypeEnum.Complementary ? <Tag color='gold'  key={'Complementară'}>Complementară</Tag>
                    : record.formativeCategory === IndicatorTypeEnum.Specialty ? <Tag color='purple'  key={'De specializare'}>De specializare</Tag>
                    : 'N/A'
            }
        },
        {
            title: 'Tip disciplină',
            dataIndex: 'subjectCategory',
            key: 'subjectCategory',
            render: (text: any, record: any, index: number) => {
                return record.subjectCategory === IndicatorTypeEnum.Mandatory ? <Tag color='gold'  key={'Obligatorie'}>Obligatorie</Tag>
                    : record.subjectCategory === IndicatorTypeEnum.Optional ? <Tag color='green'  key={'Opțională'}>Opțională</Tag>
                    : record.subjectCategory === IndicatorTypeEnum.Facultative ? <Tag color='orange'  key={'Facultativă'}>Facultativă</Tag>
                    : 'N/A'
            }
        },
        ...(program?.isDualProgram
            ? [
                {
                    title: 'Nr. ECTS',
                    key: 'dualCredits',
                    children: [
                        {
                            title: 'IIS',
                            dataIndex: 'creditsIIS',
                            key: 'creditsIIS',
                            render: (value: number) => ({
                                props: {
                                    style: {
                                        background: value > 0 ? dualProgramHoursUniversityColor : "transparent",
                                    },
                                },
                                children: <div>{value !== 0 ? value : null}</div>,
                            }),
                        },
                        {
                            title: 'OE',
                            dataIndex: 'creditsOE',
                            key: 'creditsOE',
                            render: (value: number) => ({
                                props: {
                                    style: {
                                        background: value > 0 ? dualProgramHoursPartnerColor : "transparent",
                                    },
                                },
                                children: <div>{value !== 0 ? value : null}</div>,
                            }),
                        },
                    ],
                },
            ]
            : [
                {
                    title: 'Nr. ECTS',
                    dataIndex: 'credits',
                    key: 'credits',
                },
            ]),
        {
            title: 'Ore/Săptămână',
            key: 'weeklyHours',
            children: [
                {
                title: 'Curs',
                dataIndex: 'courseWeeklyHours',
                key: 'courseWeeklyHours',
                render: (text: string, record: any, index: number) =>
                    {
                        return {
                            props: {
                                style: { background: record.courseWeeklyHours < 0 ? "#F8D7DA" : "transparent" }
                            },
                            children: <div>{record.courseWeeklyHours !==0 ? record.courseWeeklyHours : null}</div>
                        };
                    }
                },
                {
                    title: 'Seminar',
                    dataIndex: 'seminarWeeklyHours',
                    key: 'seminarWeeklyHours',
                render: (text: string, record: any, index: number) =>
                    {
                        return {
                            props: {
                                style: { background: getDualHourBackground(record.seminarWeeklyHours, record.seminarAtPartner) }
                            },
                            children: <div>{record.seminarWeeklyHours !==0 ? record.seminarWeeklyHours : null}</div>
                        };
                    }
                },
                {
                title: 'Laborator',
                dataIndex: 'labWeeklyHours',
                key: 'labWeeklyHours',
                render: (text: string, record: any, index: number) =>
                    {
                        return {
                            props: {
                                style: { background: getDualHourBackground(record.labWeeklyHours, record.labAtPartner) }
                            },
                            children: <div>{record.labWeeklyHours !==0 ? record.labWeeklyHours : null}</div>
                        };
                    }
                },
                {
                title: studyCycle == StudyCycleEnum.Bachelor ? 'Proiect' : 'P/ Cer.',
                dataIndex: 'projectWeeklyHours',
                key: 'projectWeeklyHours',
                render: (text: string, record: any, index: number) =>
                    {
                        return {
                            props: {
                                style: { background: getDualHourBackground(record.projectWeeklyHours, record.projectAtPartner) }
                            },
                            children: <div>{record.projectWeeklyHours !==0 ? record.projectWeeklyHours : null}</div>
                        };
                    }
                },
            ],
        },
        {
            title: 'Total Ore',
            key: 'totalHours',
            children: [
                {
                    title: 'Activități Didactice',
                    dataIndex: 'tutoringHours',
                    key: 'tutoringHours',
                    onCell: (record: any) => {
                        if (record.disciplineCategory === DisciplineCategoryEnum.PracticeStage) {
                            return { colSpan: 2 };
                        }
                        return {};
                    },
                    render: (value: any, record: any) => {
                        if (record.disciplineCategory === DisciplineCategoryEnum.PracticeStage) {
                            return {
                                props: {
                                    style: { background: getDualHourBackground(record.practiceTotalHours, record.practiceAtPartner) }
                                },
                                children: <div>{record.practiceTotalHours !==0 ? record.practiceTotalHours : null}</div>
                            };
                        }
                        return {
                            props: {
                                style: { background: value < 0 ? "#F8D7DA" : "transparent" }
                            },
                            children: <div>{value !==0 ? value : null}</div>
                        };
                    },
                },
                {
                    title: 'Studiu individual',
                    dataIndex: 'selfStudyHours',
                    key: 'selfStudyHours',
                    onCell: (record: any) => {
                        if (record.disciplineCategory === DisciplineCategoryEnum.PracticeStage) {
                            return { colSpan: 0 };
                        }
                        return {};
                    },
                    render: (text: string, record: any, index: number) =>
                    {
                        return {
                            props: {
                                style: { background: record.selfStudyHours < 0 ? "#F8D7DA" : "transparent" }
                            },
                            children: <div>{record.selfStudyHours !==0 ? record.selfStudyHours : null}</div>
                        };
                    }
                },
            ],
        },
        {
            title: 'Forma de Evaluare',
            dataIndex: 'evaluationType',
            key: 'evaluationType',
        }
    ];

    const isProgramSupervisor = program?.programSupervisors?.some(supervisor => supervisor.id === getUserId());

    const canEditPlan = isCadru && 
    (canManageDoctorate
        //Daca e draft poate fi editat de directorul de departament, decan sau supervizor de program
        || (selectedPlan?.status === StudyPlanStatusEnum.Draft && (isPlanDepartmentDirector || isDean || isProgramSupervisor))
        //Daca e pending dean validation poate fi editat de decan
        || (selectedPlan?.status === StudyPlanStatusEnum.PendingDeanValidation && (isDean)));
    if (!isCadru) {
        columns.push({
            title: "Status",
            dataIndex: "status", // de modificat campul de status
            key: "status",
            render: (_: any, record: SubjectDTO) => {
                const status = record.status ?? SubjectStatusEnum.Draft; // campul de status

                return (
                <Tag color={statusColors(status)} key={status}>
                    {statusToString(status)}
                </Tag>
                );
            },
        });
    }

    if (canEditPlan) {
        columns.push( {
            title: 'Rezultatele învățării',
            key: 'learnings',
            render: (text: string, record: any) =>
                (
                    <Tooltip title='Rezultatele învățării'>
                        <FontAwesomeIcon
                            icon={solid('edit')}
                            style={{
                                marginLeft: '20px',
                                cursor: 'pointer',
                                height: '1rem',
                                padding: '6px 0px',
                            }}
                            onClick={() => handleShowLearningsDetails(record)}
                        />
                    </Tooltip>
                ),
        });

    }

    if (canEditPlan && program?.isEurAceCertified) {
        columns.push( {
            title: 'Arii EAFSG',
            key: 'eafsgAreas',
            render: (text: string, record: any) =>
                (
                    <Tooltip title='Arii EAFSG'>
                        <FontAwesomeIcon
                            icon={solid('edit')}
                            style={{
                                marginLeft: '20px',
                                cursor: 'pointer',
                                height: '1rem',
                                padding: '6px 0px',
                            }}
                            onClick={() => handleShowEafsgAreasDetails(record)}
                        />
                    </Tooltip>
                ),
        });

    }


    
    if (canEditPlan) { 
        columns.push(
            {
                dataIndex: 'actiuni',
                title: 'Acțiuni',
                width: '100px',
                key: 'actions',
                render: (text: string, record: any) => (
                    <div>
                        <Link to={{ pathname: '/' + studyCycle + '/editare-disciplina-short/' + programId + "/" + record.id, state:  selectedPlan}}>
                            <Tooltip title='Editare disciplină'>
                                <FontAwesomeIcon
                                    icon={solid('edit')}
                                    style={{
                                        cursor: 'pointer',
                                        height: '1rem',
                                        padding: '6px 0px',
                                    }}
                                />
                            </Tooltip>
                        </Link>
                        <Tooltip title='Ștergere'>
                            <FontAwesomeIcon
                                icon={solid('trash')}
                                style={{
                                    marginLeft: '20px',
                                    cursor: 'pointer',
                                    height: '1rem',
                                    padding: '6px 0px',
                                }}
                                onClick={() => handleShowDelete(record.id)}
                            />
                        </Tooltip>
                        
                    </div>
                )
            }
        );
    }

    const isFacultyManaged = selectedPlan?.departmentId === '00000000-0000-0000-0000-000000000000';
    
    // TODO update logic based on user role and flags
    if (!isCadru) { 
        columns.push( {
            title: 'Laborator',
            key: 'laboratory',
            render: (text: string, record: any) =>
                record.labWeeklyHours > 0 ? (
                    <Tooltip title='Laborator'>
                        <FontAwesomeIcon
                            icon={solid('display')}
                            style={{
                                marginLeft: '20px',
                                cursor: 'pointer',
                                height: '1rem',
                                padding: '6px 0px',
                            }}
                            onClick={() => handleShowDetails(record)}
                        />
                    </Tooltip>
                ) : null,
        });
    }
    if (!isCadru || program?.accreditationType === AccreditationTypeEnum.Infiintare) { 

        const isTitular = (record: Subject) => record.courseCoordinators?.some(c => c.id === getUserId());
        const isLabTitular = (record: Subject) => record.labCoordinators?.some(c => c.id === getUserId());

        // Mirrors SubjectController.GetStatusHistory's canView exactly: course titular, lab titular,
        // program supervisor, director of the subject's OR the plan's department (isSubjectDepartmentDirector,
        // same as IsDepartmentDirectorOfSubject), dean of the program's faculty, admins. No CSUD-director
        // bypass — the backend doesn't grant one either, even for doctorate programs.
        const showViewHistory = (record: Subject) => (
            isAdmin ||
            isDean ||
            isSubjectDepartmentDirector(record) ||
            isProgramSupervisor ||
            isTitular(record) ||
            isLabTitular(record)
        );

        const showSendMessage = () => isProgramSupervisor;

        // Aceleasi reguli ca SubjectController.AssignCoordinators; independent de statusul planului/fisei
        const canAssignCoordinators = (record: any) => (
            canManageDoctorate ||
            isDean ||
            isSubjectDepartmentDirector(record) ||
            isProgramSupervisor
        );


        const canEditSubject = (record: any) => {
            
            switch (record.status) {
                case SubjectStatusEnum.Draft:
                    return (
                        canManageDoctorate ||
                        isDean ||
                        isSubjectDepartmentDirector(record) ||
                        isProgramSupervisor ||
                        isTitular(record) ||
                        (record.courseWeeklyHours === 0 && isLabTitular(record))
                    );

                case SubjectStatusEnum.PendingDirectorValidation:
                    return (
                        canManageDoctorate ||
                        isDean ||
                        isSubjectDepartmentDirector(record) ||
                        isProgramSupervisor
                    );

                case SubjectStatusEnum.PendingDeanValidation:
                    return (
                        canManageDoctorate ||
                        isDean
                    );

                case SubjectStatusEnum.PendingQaValidation:
                    return canManageDoctorate;

                case SubjectStatusEnum.Approved:
                    return canManageDoctorate;

                default:
                    return false;
            }
        };

        const showRejectSubject = (record: any) => {
            if (canManageDoctorate) {
                return record.status !== SubjectStatusEnum.Draft;
            }

            if (isSubjectDepartmentDirector(record)) {
                return record.status === SubjectStatusEnum.PendingDirectorValidation;
            }

            if (isDean) {
                return record.status === SubjectStatusEnum.PendingDeanValidation;
            }

            return false;
        };

        const showApproveDiscipline = (record: Subject) => {
            const isPartiallySaved = (record as any).isPartiallySaved;
            
            switch (record.status) {
                case SubjectStatusEnum.Draft:
                    if (!isTitular(record) && !(record.courseWeeklyHours === 0 && isLabTitular(record))) return null;

                    return {
                        nextStatus: (record.departmentId === null || record.departmentId === undefined || record.departmentId === '00000000-0000-0000-0000-000000000000' )
                        ? SubjectStatusEnum.PendingDeanValidation
                        : SubjectStatusEnum.PendingDirectorValidation,
                        disabled: !!isPartiallySaved,
                        disabledReason: "Fișa este salvată parțial. Faceți o salvare normală înainte de trimitere spre validare.",
                    };

                case SubjectStatusEnum.PendingDirectorValidation:
                    if (!isSubjectDepartmentDirector(record)) return null;

                    return {
                        nextStatus: SubjectStatusEnum.PendingDeanValidation,
                        disabled: false,
                    };

                case SubjectStatusEnum.PendingDeanValidation:
                    if (!isDean) return null;

                    return {
                        nextStatus: SubjectStatusEnum.PendingQaValidation,
                        disabled: false,
                    };

                case SubjectStatusEnum.PendingQaValidation:
                    if (!canManageDoctorate) return null;

                    return {
                        nextStatus: SubjectStatusEnum.Approved,
                        disabled: false,
                    };

                default:
                    return null;
            }
        };
        columns.push(
        {
            title: 'Fișă disciplină',
            key: 'file',
            width: '200px',
            render: (text: string, record: any) =>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Tooltip title='Descarcare'>
                            <FontAwesomeIcon
                                icon={solid('file-pdf')}
                                style={{
                                    cursor: 'pointer',
                                    height: '1rem',
                                    padding: '6px 0px',
                                }}
                                onClick={() => handlePdfDownload(record)}
                            />
                        </Tooltip>
                        <Tooltip title='Vizualizare'>
                            <FontAwesomeIcon
                                icon={solid('eye')}
                                style={{
                                    marginLeft: '20px',
                                    cursor: 'pointer',
                                    height: '1rem',
                                    padding: '6px 0px',
                                }}
                                onClick={() => handlePdfPreview(record)}
                            />
                        </Tooltip>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center' }}>

                        {canEditSubject(record) && (
                            <Link to={{
                                pathname: '/' + studyCycle + '/editare-disciplina/' + programId + "/" + record.id,
                                state: { plan: selectedPlan, from: '/' + studyCycle + '/plan/' + programId }
                            }}>
                                <Tooltip title='Editare fișă disciplină'>
                                    <FontAwesomeIcon
                                        icon={solid('edit')}
                                        style={{
                                            cursor: 'pointer',
                                            height: '1rem',
                                            padding: '6px 0px',
                                        }}
                                    />
                                </Tooltip>
                            </Link>
                        )}
                        {showApproveDiscipline(record) != null && (
                            <Tooltip title={showApproveDiscipline(record)?.disabled ? showApproveDiscipline(record)?.disabledReason : 'Aprobare'}>
                                <FontAwesomeIcon
                                    icon={solid('check')}
                                    style={{
                                        marginLeft: '20px',
                                        cursor: showApproveDiscipline(record)?.disabled ? 'not-allowed' : 'pointer',
                                        height: '1rem',
                                        padding: '6px 0px',
                                        opacity: showApproveDiscipline(record)?.disabled ? 0.5 : 1,
                                    }}
                                    onClick={() => {
                                        if (showApproveDiscipline(record)?.disabled) return;
                                        handleApproveDiscipline(
                                            showApproveDiscipline(record)?.nextStatus ?? SubjectStatusEnum.Draft,
                                            'Aprobare disciplină',
                                            record.id
                                        )
                                    }}
                                />
                            </Tooltip>
                        )}

                        {showRejectSubject(record) && (
                            <Tooltip title='Respingere'>
                                <FontAwesomeIcon
                                    icon={solid('times')}
                                    style={{
                                        marginLeft: '20px',
                                        cursor: 'pointer',
                                        height: '1rem',
                                        padding: '6px 0px',
                                    }}
                                    onClick={() => handleRejectDiscipline(record)}
                                />
                            </Tooltip>
                        )}

                        {showSendMessage() && (
                            <Tooltip title='Transmite mesaj'>
                                <FontAwesomeIcon
                                    icon={solid('comment-dots')}
                                    style={{
                                        marginLeft: '20px',
                                        cursor: 'pointer',
                                        height: '1rem',
                                        padding: '6px 0px',
                                    }}
                                    onClick={() => handleShowSendMessage(record)}
                                />
                            </Tooltip>
                        )}

                        {showViewHistory(record) && (
                            <Tooltip title='Istoric status'>
                                <FontAwesomeIcon
                                    icon={solid('clock-rotate-left')}
                                    style={{
                                        marginLeft: '20px',
                                        cursor: 'pointer',
                                        height: '1rem',
                                        padding: '6px 0px',
                                    }}
                                    onClick={() => handleShowHistory(record)}
                                />
                            </Tooltip>
                        )}

                        {canAssignCoordinators(record) && (
                            <Tooltip title='Asignare titulari'>
                                <FontAwesomeIcon
                                    icon={solid('users')}
                                    style={{
                                        marginLeft: '20px',
                                        cursor: 'pointer',
                                        height: '1rem',
                                        padding: '6px 0px',
                                    }}
                                    onClick={() => setCoordinatorsSubject({ id: record.id, nameRO: record.nameRO })}
                                />
                            </Tooltip>
                        )}
                    </div>

                </div>
        });
    }


    const handleSemesterChange = (value: SemesterTypeEnum) => {
        setSemester(value);
    }

    const handleStudyYearChange = (value: StudyYearEnum) => {
        setStudyYear(value);
    }

    const handlePlanChange = (value: string) => {
        if (value.includes('cadru')) {
            setPlanYear(parseInt(value.split(' ')[1]));
            setSchoolYear(undefined);
            setIsCadru(true);
            setFacultyId(plans?.data?.find(plan => plan.planYear === parseInt(value.split(' ')[1]))?.facultyId);
            //setSelectedPlan(plans?.data?.find(plan => plan.planYear === parseInt(value.split(' ')[1])));
        } else {
            setSchoolYear(parseInt(value));
            setPlanYear(undefined);
            setIsCadru(false);
        }

    }
    
    const handleStatusChangeDiscipline = (status: SubjectStatusEnum, message: string, subjectId: string) => async () => {
        try {
            await updateSubjectStatus(subjectId, status, message);
            success('Statusul a fost actualizat cu succes!');
            queryClient.invalidateQueries(['getSubjects']);
        } catch (err: any) {
            const validationErrors = await parseSubjectStatusValidationErrors(err);
            if (validationErrors) {
                setModalPropsValidationErrorsDiscipline({ isModalVisible: true, errors: validationErrors });
                return;
            }
            let errMsg = await err.text();
            failed(err, 'Statusul nu a putut fi actualizat! ' + errMsg);
        }
    };

    const handleStatusChange = (status: StudyPlanStatusEnum, message: string) => async () => {
        if (!selectedPlan) return;
        try {
            await updateStudyPlanStatus(selectedPlan.id!, status, message);
            setSelectedPlan(p => (p ? { ...p, status } : p));
            success('Statusul a fost actualizat cu succes!');
            queryClient.invalidateQueries(['getAllStudyPlans', programId]);
        } catch (err: any) {
            try {
                const validationResult = await err.clone().json();
                if (validationResult && (
                    validationResult.generalErrors?.length
                    || validationResult.subjectsMissingLearningResults?.length
                    || validationResult.competencesMissingLearningResults?.length
                    || validationResult.subjectsMissingEAFSGAreas?.length
                )) {
                    setModalPropsValidationErrors({
                        isModalVisible: true,
                        generalErrors: validationResult.generalErrors ?? [],
                        subjectsMissingLearningResults: validationResult.subjectsMissingLearningResults ?? [],
                        competencesMissingLearningResults: validationResult.competencesMissingLearningResults ?? [],
                        subjectsMissingEAFSGAreas: validationResult.subjectsMissingEAFSGAreas ?? [],
                    });
                    return;
                }
            } catch {
                // not a structured validation error, fall back to plain text below
            }
            let errMsg = await err.text();
            failed(err, 'Statusul nu a putut fi actualizat! ' + errMsg);
        }
    };

    const handleDepartmentChange = (value: string) => {
        if (!!selectedPlan) {
            updateStudyPlanDepartment(selectedPlan.id!, value)
            .then(() => {
                success('Departamentul a fost actualizat cu succes!');
                invalidateQuery();
            })
            .catch((err) => failed(err, 'Departamentul nu a putut fi actualizat!'));
        }
    }

    const { data: labs, isLoading: isLoadingLabSearch } = useQuery(['getLaboratories', debouncedLabSearch],
        () => {
            const response = getLaboratoriesByUserFaculty(undefined, undefined, debouncedLabSearch, 1, 20);
            
            return response;
        },
        {
            onError: openFetchError,
        }
    );


    const { data: learnings, isLoading: isLoadingLearningSearch } = useQuery(
        ['getLearnings', debouncedLearningSearch, selectedPlan?.id],
        () => getLearningCompetences({
            name: debouncedLearningSearch,
            type: LearningCompetenceSimpleTypeEnum.Learning,
            page: 1,
            pageSize: 100,
            programId,
            studyPlanId: selectedPlan?.id
        }),
        { 
            enabled: !!selectedPlan?.id,
            onError: openFetchError 
        }
        );

    const filteredLearnings = useMemo(() => {
        const subjectType = modalPropsLearnings.record.formativeCategory;
        const allLearnings = learnings?.data || [];
        
        if (subjectType === IndicatorTypeEnum.Fundamental && planYear && planYear > 2025) {
            // Master's fundamental subjects may additionally correlate with "own" (non-template) results.
            if (studyCycle === StudyCycleEnum.Master) {
                return allLearnings.filter((learning) => !learning.isCompetenceTemplate);
            }
            return allLearnings.filter((learning) =>
                learning.isDomainLearningOutcomeTemplate || learning.isCompetenceTemplateAssociatedOutcome);
        }

        if (subjectType === IndicatorTypeEnum.Specialty || subjectType === IndicatorTypeEnum.Complementary || (planYear && planYear <= 2025)) {
            return allLearnings.filter((learning) =>
                !learning.isDomainLearningOutcomeTemplate
                && !learning.isCompetenceTemplate);
        }

        return allLearnings;
    }, [learnings?.data, modalPropsLearnings.record.formativeCategory, studyCycle]);

    const { data: assignedLearningsData } = useQuery(
        ['getSubjectFileForLearnings', modalPropsLearnings.record.id],
        () => getSubject(modalPropsLearnings.record.id!),
        {
            enabled: modalPropsLearnings.isModalVisible && !!modalPropsLearnings.record.id,
            onError: openFetchError
        }
    );

    const learningSelectOptions = useMemo(() => {
        const alreadyAssigned = assignedLearningsData?.learningResultsData || [];

        return [
            ...alreadyAssigned,
            ...filteredLearnings.filter(learning => !alreadyAssigned.some((assigned: LearningCompetenceDTO) => assigned.id === learning.id))
        ];
    }, [filteredLearnings, assignedLearningsData?.learningResultsData]);

    const { data: eafsgAreas, isLoading: isLoadingEafsgAreaSearch } = useQuery(
        ['getEAFSGAreas', debouncedEafsgAreaSearch],
        () => getEAFSGAreas(debouncedEafsgAreaSearch, 1, 100),
        {
            enabled: !!program?.isEurAceCertified,
            onError: openFetchError
        }
    );


    
    const status = selectedPlan?.status;    

    const canAdvanceFromDraft = status === StudyPlanStatusEnum.Draft &&
    (
        (!isFacultyManaged &&
            (isPlanDepartmentDirector || isProgramSupervisor)
        ) ||
        (isFacultyManaged &&
            (isDean || isProgramSupervisor)
        )
    );
    const nextStatusFromDraft = isFacultyManaged
        ? StudyPlanStatusEnum.PendingQaValidation
        : StudyPlanStatusEnum.PendingDeanValidation;


    const canAdvanceFromPendingDean = status === StudyPlanStatusEnum.PendingDeanValidation && isDean;

    const canAdvanceFromPendingQA = status === StudyPlanStatusEnum.PendingQaValidation && canManageDoctorate;

    const canApproveFinal = status === StudyPlanStatusEnum.PendingFacultyCouncilApproval && isDean;

    const canRejectFromPendingDean = status === StudyPlanStatusEnum.PendingDeanValidation && isDean;

    const canRejectFromPendingQA = status === StudyPlanStatusEnum.PendingQaValidation && canManageDoctorate;

    const rejectTargetFromPendingQA: StudyPlanStatusEnum = isFacultyManaged
        ? StudyPlanStatusEnum.Draft
        : StudyPlanStatusEnum.PendingDeanValidation;

    const studyYearOptions = getStudyYearOptions(studyCycle);
    const doctorateSemesterOptions = (
        <>
            <Option key={SemesterTypeEnum.S1} value={SemesterTypeEnum.S1}>S1</Option>
            <Option key={SemesterTypeEnum.S2} value={SemesterTypeEnum.S2}>S2-8</Option>
        </>
    );

    return (
        <div className={styles.container} style={{ display: "flex", flexDirection: "column", marginTop: "0%" }}>
            <Layout>
                {program?.name && <Typography.Title level={4} style={{ marginBottom: '16px' }}>{program.name}</Typography.Title>}
                <div style={{ marginBottom: '20px', display: 'flex', gap: '20px' }}>
                    {isDoctorate ? null : (
                        <Select defaultValue={filters.year}
                            placeholder="Anul de Studiu"
                            style={{ width: '200px' }}
                            onChange={(year) => {
                                handleStudyYearChange(year)
                                setFilters({...filters, year: year})}
                            }
                        >
                            {studyYearOptions}
                        </Select>
                    )}
                    <Select defaultValue={filters.semester}
                        placeholder="Semestrul"
                        style={{ width: '200px' }}
                        onChange={(semester) => {
                            handleSemesterChange(semester)
                            setFilters({...filters, semester: semester})}
                        }
                    >
                        {isDoctorate ? doctorateSemesterOptions : semesterOptions}
                    </Select>
                    {/*  */}
                    <Select value={filters.plan}
                        placeholder="Plan curent/cadru"
                        style={{ width: '200px' }}
                        onChange={(plan) => {
                            handlePlanChange(plan)
                            setFilters({...filters, plan: plan})}
                        }
                        defaultActiveFirstOption
                    >
                        {planOptions(plans?.data || [], isDoctorate)}
                    </Select>
                    {canEditPlan ? <div>
                        <Tooltip title="Adaugă Disciplină">
                            <Link to={{ pathname: '/' + studyCycle + '/adaugare-disciplina-short/' + programId, state: selectedPlan}}>
                                <Button type="primary" icon={<PlusOutlined />}>
                                    Adaugă Disciplină
                                </Button>
                            </Link>
                        </Tooltip>
                    </div>
                    : null}
                    {canEditPlan && !isDoctorate ? <Select
                        style={{ width: '600px' }}
                        onChange={handleDepartmentChange}
                        value={plans?.data?.find(plan => plan.planYear === planYear)?.departmentId}
                        defaultActiveFirstOption
                    >
                        {[{ id: '00000000-0000-0000-0000-000000000000', departmentNameRo: 'Facultate'} as DepartmentDTO].concat(departments || [])
                        .map(department => (
                            <Option key={department.id} value={department.id}>
                                {department.departmentNameRo}
                            </Option>
                        ))}
                    </Select>
                    : null}
                </div>
                {isCadru && (isDean || isDepartmentDirector || canManageDoctorate || isProgramSupervisor)
                ? <div style={{ marginBottom: '20px', display: 'flex', gap: '20px' }}>
                    <Tooltip title="Indicatori">
                        <Link to={{ pathname: '/statusIndicatori/' + programId + '/' + planYear}}>
                            <Button type="primary" icon={<BarChartOutlined />} style={{ width: '200px' }}>
                                Indicatori
                            </Button>
                        </Link>
                    </Tooltip>
                    <Tooltip title="Listă discipline">
                            <Button type="primary" icon={<DownloadOutlined />} style={{ width: '200px' }} onClick={() => handleStudyPlanPdfDownload(selectedPlan?.id!)}>
                                Listă discipline
                            </Button>
                    </Tooltip>
                    {isCadru && (isDean || isDepartmentDirector || canManageDoctorate || isProgramSupervisor)
                     && <Tooltip title="Plan învățământ">
                            <Button type="primary" icon={<DownloadOutlined />} style={{ width: '200px' }} onClick={() => handleExtendedStudyPlanPdfDownload(selectedPlan?.id!)}>
                                Plan învățământ
                            </Button>
                    </Tooltip>}
                    {isCadru && (isDean || isDepartmentDirector || canManageDoctorate || isProgramSupervisor)
                     && <Tooltip title="Previzualizare Plan învățământ">
                            <Button type="primary" icon={<EyeOutlined />} style={{ width: '200px' }} onClick={() => handleExtendedStudyPlanPdfPreview(selectedPlan?.id!)}>
                                Previzualizare plan
                            </Button>
                    </Tooltip>}

                    {isCadru && (isDean || isDepartmentDirector || canManageDoctorate)
                     && <Tooltip title="Raport indicatori">
                            <Button type="primary" icon={<DownloadOutlined />} style={{ width: '200px' }} onClick={() => handleStudyPlanIndicatorsPdfDownload(selectedPlan?.id!)}>
                                Raport indicatori
                            </Button>
                    </Tooltip>}
                    {isCadru && isAdmin
                     && <Tooltip title={hasPreviousPlan
                            ? 'Modificări față de planul cadru anterior'
                            : 'Nu există un plan cadru anterior cu care să fie comparat'}>
                            <span>
                                <Button type="primary" icon={<DownloadOutlined />} style={{ width: '200px' }}
                                    disabled={!hasPreviousPlan}
                                    onClick={() => handleStudyPlanDiffPdfDownload(selectedPlan?.id!)}>
                                    Raport modificări
                                </Button>
                            </span>
                    </Tooltip>}
                    {canEditPlan && <Tooltip title="Grupuri Discipline">
                        <Link to={{ pathname: '/grupuriDiscipline/' + selectedPlan?.id }}>
                            <Button type="primary" icon={<GroupOutlined />} style={{ width: '200px' }}>
                                Grupuri Discipline
                            </Button>
                        </Link>
                    </Tooltip>}

                    {canAdvanceFromDraft && (
                        <Button
                            type="primary"
                            onClick={handleStatusChange(nextStatusFromDraft, "Trimitere spre validare")}
                        >
                            Trimitere spre validare
                        </Button>
                    )}

                    {canAdvanceFromPendingDean && (
                        <Button
                            type="primary"
                            onClick={handleStatusChange(StudyPlanStatusEnum.PendingQaValidation, "Trimitere spre validare DEAC")}
                        >
                            Trimitere spre validare DEAC
                        </Button>
                    )}

                    {canAdvanceFromPendingQA && (
                        <Button
                            type="primary"
                            onClick={handleStatusChange(StudyPlanStatusEnum.PendingFacultyCouncilApproval, "Aprobare calitate, trimitere către Consiliul Facultății")}
                        >
                            Trimitere către Consiliul Facultății
                        </Button>
                    )}

                    {canApproveFinal && (
                        <Button
                            type="primary"
                            onClick={handleStatusChange(StudyPlanStatusEnum.Approved, "Avizare Consiliul Facultății")}
                        >
                            Avizare Consiliul Facultății    
                        </Button>
                    )}

                    {canRejectFromPendingDean && (
                        <Button
                            danger
                            onClick={() => handleShowReject(StudyPlanStatusEnum.Draft)}
                        >
                            Respinge plan
                        </Button>
                    )}

                    {canRejectFromPendingQA && (
                        <Button
                            danger
                            onClick={() => handleShowReject(rejectTargetFromPendingQA)}
                        >
                            Respinge plan
                        </Button>
                    )}

                    {/* TODO bind buttons to endpoints */}
                    {(selectedPlan?.status !== StudyPlanStatusEnum.Approved && canManageDoctorate)
                     && <Tooltip title="Aprobare plan">
                        <Button type="primary" icon={<CheckCircleOutlined />} style={{ width: '200px' }} onClick={handleStatusChange(StudyPlanStatusEnum.Approved, 'Aprobare directa plan - admin')}>
                            Aprobare plan
                        </Button>
                    </Tooltip>}

                    {(selectedPlan?.status !== StudyPlanStatusEnum.Draft && canManageDoctorate)
                     && <Tooltip title="Resetare status plan">
                        <Button type="primary" icon={<ReloadOutlined />} style={{ width: '200px' }} onClick={handleStatusChange(StudyPlanStatusEnum.Draft, 'Resetare status plan - admin')}>
                            Resetare status plan
                        </Button>
                    </Tooltip>}
                    </div>
                    
                : null}
                {!isCadru ? <div style={{ marginBottom: '20px', display: 'flex', gap: '20px' }}>
                    
                    <Tooltip title="Laboratoare" >
                        {/* <Link to={{ pathname: '/' + studyCycle + '/adaugare-disciplina-short/' + programId}}> */}
                        <Link to={{ pathname: '/laboratoarePlan/' + programId + '/' + schoolYear}}>
                            <Button type="primary" icon={<DesktopOutlined />} style={{ width: '200px' }}>
                                Laboratoare
                            </Button>
                        </Link>
                    </Tooltip>
                    <Tooltip title="Continut Lab" >
                        <Link to={{ pathname: '/laboratoareContent/' + programId + '/' + schoolYear}}>
                            <Button type="primary" icon={<ProfileOutlined />} style={{ width: '200px' }}>
                                Continut Lab
                            </Button>
                        </Link>
                    </Tooltip>
                    <Tooltip title="Echipament Lab" >
                        <Link to={{ pathname: '/laboratoareEchipament/' + programId + '/' + schoolYear}}>
                            <Button type="primary" icon={<ApiOutlined />} style={{ width: '200px' }}>
                                Echipament Lab
                            </Button>
                        </Link>
                    </Tooltip>
                    <Tooltip title="Capacitate Lab" >
                        <Link to={{ pathname: '/laboratoareCapacitate/' + programId + '/' + schoolYear}}>
                            <Button type="primary" icon={<UserOutlined />} style={{ width: '200px' }}>
                                Capacitate Lab
                            </Button>
                        </Link>
                    </Tooltip>
                    {(isDean || isDepartmentDirector || canManageDoctorate)
                     && <Tooltip title="Aprobare fișe discipline">
                            <Button type="primary" icon={<CheckCircleOutlined />} style={{ width: '200px' }} onClick={() => handleApproveBulkSubjects(programId!)}>
                                Aprobare fișe discipline
                            </Button>
                    </Tooltip>}
                        
                </div>
                : null}
                <Modal
                    open={modalPropsRejectDiscipline.isModalVisible}
                    onOk={handleOkRejectDiscipline}
                    onCancel={handleCloseRejectDiscipline}
                    title={modalPropsRejectDiscipline.title}
                    width="500px"
                    okText={t('account.confirm')}
                    cancelText={t('account.cancel')}
                    cancelButtonProps={{
                        style: {
                            background: theme.green,
                            border: theme.green,
                            color: theme.white,
                            borderRadius: '10px'
                        }
                    }}
                    okButtonProps={{
                        style: modalPropsRejectDiscipline.message?.trim() ? {
                            background: theme.secondColor,
                            border: theme.secondColor,
                            borderRadius: '10px'
                        } : {
                            background: theme.disabledGray,
                            border: theme.disabledGray,
                            color: 'rgba(0, 0, 0, 0.25)',
                            borderRadius: '10px',
                            cursor: 'not-allowed'
                        },
                        disabled: !modalPropsRejectDiscipline.message?.trim()
                    }}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <Typography>
                            Vă rugăm să introduceți motivul respingerii fișei de disciplină.
                        </Typography>

                        <textarea
                            rows={4}
                            placeholder="Motiv respingere..."
                            value={modalPropsRejectDiscipline.message}
                            onChange={(e) =>
                                setModalPropsRejectDiscipline(p => ({
                                    ...p,
                                    message: e.target.value
                                }))
                            }
                        />
                    </div>
                </Modal>
                <SubjectCoordinatorsModal
                    subjectId={coordinatorsSubject?.id}
                    subjectName={coordinatorsSubject?.nameRO}
                    onClose={() => setCoordinatorsSubject(undefined)}
                />
                <SubjectMessageModal
                    open={modalPropsSendMessage.isModalVisible}
                    title={modalPropsSendMessage.title}
                    description="Completează observațiile legate de fișa disciplinei. Acestea vor fi vizibile în istoricul fișei și transmise titularului pe email."
                    message={modalPropsSendMessage.message}
                    placeholder="Observațiile dumneavoastră..."
                    onMessageChange={(message) => setModalPropsSendMessage(p => ({ ...p, message }))}
                    onOk={handleOkSendMessage}
                    onCancel={handleCloseSendMessage}
                />
                <SubjectStatusHistoryModal
                    open={modalPropsHistory.isModalVisible}
                    subjectId={modalPropsHistory.subjectId}
                    subjectName={modalPropsHistory.subjectName}
                    onCancel={handleCloseHistory}
                    statusColors={statusColors}
                    statusToString={statusToString}
                />
                <Modal
                    open={modalPropsReject.isModalVisible}
                    onOk={handleOkReject}
                    onCancel={handleCloseReject}
                    title="Respingere plan de învățământ"
                    width="500px"
                    okText={t('account.confirm')}
                    cancelText={t('account.cancel')}
                    cancelButtonProps={{
                        style: {
                            background: theme.green,
                            border: theme.green,
                            color: theme.white,
                            borderRadius: '10px'
                        }
                    }}
                    okButtonProps={{
                        style: modalPropsReject.message?.trim() ? {
                            background: theme.secondColor,
                            border: theme.secondColor,
                            borderRadius: '10px'
                        } : {
                            background: theme.disabledGray,
                            border: theme.disabledGray,
                            color: 'rgba(0, 0, 0, 0.25)',
                            borderRadius: '10px',
                            cursor: 'not-allowed'
                        },
                        disabled: !modalPropsReject.message?.trim()
                    }}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <Typography>
                            Vă rugăm să introduceți motivul respingerii planului.
                        </Typography>

                        <textarea
                            rows={4}
                            placeholder="Motiv respingere..."
                            value={modalPropsReject.message}
                            onChange={(e) =>
                                setModalPropsReject(p => ({
                                    ...p,
                                    message: e.target.value
                                }))
                            }
                        />
                    </div>
                </Modal>
                <Modal
                    open={modalPropsDelete.isModalVisible}
                    onOk={handleOkDelete}
                    onCancel={handleCloseDelete}
                    title="Ștergere"
                    width={"400px"}
                    okText={t('account.confirm')}
                    cancelText={t('account.cancel')}
                    cancelButtonProps={{ style: { background: theme.green, border: theme.green, color: theme.white, borderRadius: "10px" } }}
                    okButtonProps={{ style: { background: theme.secondColor, border: theme.secondColor, borderRadius: "10px" } }}
                >
                    <div>
                        <Typography>Sunteți sigur că doriți ștergerea disciplinei?</Typography>
                    </div>
                </Modal>
                
                <Modal
                    open={modalPropsLearnings.isModalVisible}
                    onOk={handleUpdateLearnings}
                    onCancel={handleCloseLearning}
                    title={modalPropsLearnings.title}
                    width={"500px"}
                    okText={t('account.confirm')}
                    cancelText={t('account.cancel')}
                    cancelButtonProps={{ style: { background: theme.green, border: theme.green, color: theme.white, borderRadius: "10px" } }}
                    okButtonProps={{ style: { background: theme.secondColor, border: theme.secondColor, borderRadius: "10px" } }}
                    >
                        <div>
                            <Form form={form} layout="vertical">
                                <Form.Item name="assignedLearnings">
                                    <Select
                                        className={styles.antSelect}
                                        mode="multiple"
                                        maxTagCount={20}
                                        allowClear
                                        showSearch
                                        style={{ width: "100%", textAlign: "left" }}
                                        placeholder={"Căutare rezultate învățare"}
                                        filterOption={false}
                                        onSearch={setLeariningSearchTerm}
                                        notFoundContent={isLoadingLearningSearch ? <Spin size="small" /> : null}
                                    >
                                        {learningSelectOptions.map((learning) => (
                                            <Option key={learning.id} value={learning.id}>
                                                {learning.identificator} - {learning.description}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Form>
                        </div>
                </Modal>

                <Modal
                    open={modalPropsEafsgAreas.isModalVisible}
                    onOk={handleUpdateEafsgAreas}
                    onCancel={handleCloseEafsgAreas}
                    title={modalPropsEafsgAreas.title}
                    width={"500px"}
                    okText={t('account.confirm')}
                    cancelText={t('account.cancel')}
                    cancelButtonProps={{ style: { background: theme.green, border: theme.green, color: theme.white, borderRadius: "10px" } }}
                    okButtonProps={{ style: { background: theme.secondColor, border: theme.secondColor, borderRadius: "10px" } }}
                    >
                        <div>
                            <Form form={form} layout="vertical">
                                <Form.Item name="assignedEAFSGAreas">
                                    <Select
                                        className={styles.antSelect}
                                        mode="multiple"
                                        maxTagCount={20}
                                        allowClear
                                        showSearch
                                        style={{ width: "100%", textAlign: "left" }}
                                        placeholder={"Căutare arii EAFSG"}
                                        filterOption={false}
                                        onSearch={setEafsgAreaSearchTerm}
                                        notFoundContent={isLoadingEafsgAreaSearch ? <Spin size="small" /> : null}
                                    >
                                        {(eafsgAreas?.data || []).map((area) => (
                                            <Option key={area.id} value={area.id}>
                                                {area.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Form>
                        </div>
                </Modal>

                <Modal
                    open={modalPropsValidationErrors.isModalVisible}
                    onOk={handleCloseValidationErrors}
                    onCancel={handleCloseValidationErrors}
                    title="Validarea a eșuat"
                    width={"600px"}
                    okText={t('account.confirm')}
                    cancelButtonProps={{ style: { display: "none" } }}
                    okButtonProps={{ style: { background: theme.secondColor, border: theme.secondColor, borderRadius: "10px" } }}
                    >
                        <div>
                            {modalPropsValidationErrors.subjectsMissingLearningResults.length > 0 && <>
                                <Typography.Title level={5}>Discipline fără rezultate ale învățării</Typography.Title>
                                {modalPropsValidationErrors.subjectsMissingLearningResults.map(name => <Typography key={name}>- {name}</Typography>)}
                            </>}
                            {modalPropsValidationErrors.competencesMissingLearningResults.length > 0 && <>
                                <Typography.Title level={5}>Competențe fără rezultate ale învățării</Typography.Title>
                                {modalPropsValidationErrors.competencesMissingLearningResults.map(name => <Typography key={name}>- {name}</Typography>)}
                            </>}
                            {modalPropsValidationErrors.subjectsMissingEAFSGAreas.length > 0 && <>
                                <Typography.Title level={5}>Discipline fără arii EAFSG</Typography.Title>
                                {modalPropsValidationErrors.subjectsMissingEAFSGAreas.map(name => <Typography key={name}>- {name}</Typography>)}
                            </>}
                            {modalPropsValidationErrors.generalErrors.length > 0 && <>
                                <Typography.Title level={5}>Alte erori</Typography.Title>
                                {modalPropsValidationErrors.generalErrors.map(message => <Typography key={message}>- {message}</Typography>)}
                            </>}
                        </div>
                </Modal>

                <Modal
                    open={modalPropsValidationErrorsDiscipline.isModalVisible}
                    onOk={handleCloseValidationErrorsDiscipline}
                    onCancel={handleCloseValidationErrorsDiscipline}
                    title="Validarea a eșuat"
                    width={"600px"}
                    okText={t('account.confirm')}
                    cancelButtonProps={{ style: { display: "none" } }}
                    okButtonProps={{ style: { background: theme.secondColor, border: theme.secondColor, borderRadius: "10px" } }}
                    >
                        <div>
                            {modalPropsValidationErrorsDiscipline.errors.map(message => <Typography key={message}>- {message}</Typography>)}
                        </div>
                </Modal>

                <Modal
                    open={modalProps.isModalVisible}
                    onOk={handleUpdateLabs}
                    onCancel={handleClose}
                    title={modalProps.title}
                    width={"500px"}
                    okText={t('account.confirm')}
                    cancelText={t('account.cancel')}
                    cancelButtonProps={{ style: { background: theme.green, border: theme.green, color: theme.white, borderRadius: "10px" } }}
                    okButtonProps={{ style: { background: theme.secondColor, border: theme.secondColor, borderRadius: "10px" } }}
                    >
                        <div>
                            <Form form={form} layout="vertical">
                                <Form.Item name="assignedLabs">
                                    <Select
                                        className={styles.antSelect}
                                        mode="multiple"
                                        maxTagCount={20}
                                        allowClear
                                        showSearch
                                        style={{ width: "100%", textAlign: "left" }}
                                        placeholder={"Căutare laborator"}
                                        filterOption={false}
                                        onSearch={setLabSearchTerm}
                                        notFoundContent={
                                            isLoadingLabSearch ? <Spin size="small" /> : null
                                        }
                                    >
                                        {[
                                            ...(modalProps?.record?.laboratories || []),
                                            ...(labs?.data?.filter(lab => !(modalProps?.record?.laboratories || []).some(recordLab => recordLab.id === lab.id)) || [])
                                        ]?.map((lab) => (
                                                <Option key={lab.id} value={lab.id}>
                                                    {lab.number}
                                                </Option>
                                            ))
                                            }
                                    </Select>
                                </Form.Item> 
                            </Form>
                        </div>
                </Modal>
                <Table
                    className={styles.subjectsTable}
                    dataSource={data?.data || []}
                    bordered
                    pagination={false}
                    locale={{
                        emptyText: (
                            <>{canEditPlan && <span>
                                <h3>Nu există discipline adăugate! Apăsați butonul de mai jos pentru a adăuga o disciplină nouă!</h3>
                                <Tooltip title="Adaugă Disciplină">
                                     <Link to={{ pathname: '/' + studyCycle + '/adaugare-disciplina-short/' + programId, state: selectedPlan}}>
                                        <Button type="primary" icon={<PlusOutlined />}>
                                            Adaugă Disciplină
                                        </Button>
                                    </Link>
                                </Tooltip>
                            </span>}
                            {!isCadru && <span>
                                <h3>Nu există discipline pentru a compune planul curent! Selectați planul cadru de mai sus și adăugați disciplinele dorite.</h3>
                            </span>}
                            </>
                          )
                    }}
                    columns={columns}
                    rowKey={record => record.id!}
                    loading={isLoading}
                />
                {program?.isDualProgram && (
                    <div style={{ marginTop: "12px", fontWeight: 600 }}>
                        Total ECTS-IIS: {dualCreditsTotals.iis} | Total ECTS-OE: {dualCreditsTotals.oe}
                    </div>
                )}
            </Layout>
        </div>
    );
}

export default Subjects;